import { supabase } from '../../config/supabase';

/**
 * Get Australian date
 */
const getAustralianDate = () => {
    return new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
};

/**
 * Fetch scheduled deliveries for a specific date
 * @param {string} companyId - The company ID
 * @param {Date} date - The target date (should be a Date object in local/Melbourne time)
 * @param {Object} filters - Filter options (userId, showOnlyMine)
 * @returns {Promise<Object>} Deliveries for the specific date
 */
export const getDeliveriesForDate = async (companyId, date, filters = {}) => {
    // Normalize the incoming date to start of day
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    // Format the date for SQL query (YYYY-MM-DD format)
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    return getDeliveriesInRange(companyId, dateString, dateString, filters);
};

/**
 * Fetch scheduled, undelivered deliveries between two past dates (inclusive).
 * Same card shape and filters as getDeliveriesForDate; playground cards are excluded.
 * @param {string} companyId - The company ID
 * @param {Object} filters - Filter options (userId, showOnlyMine, productionFilter, quoteTypeFilters)
 * @param {{ start: string, end: string }} range - YYYY-MM-DD bounds, e.g. a month ago → yesterday
 * @returns {Promise<Object>} Overdue delivery cards, oldest first
 */
export const getOverdueDeliveries = async (companyId, filters = {}, { start, end }) => {
    const result = await getDeliveriesInRange(companyId, start, end, filters);
    if (result.error) return result;

    const overdue = result.data
        .filter(card => card.delivery?.delivery_status !== 'delivered')
        .sort((a, b) =>
            (a.quote_info?.expected_finish_date || '').localeCompare(b.quote_info?.expected_finish_date || '')
        );

    return { data: overdue, error: null };
};

/**
 * Fetch scheduled deliveries whose expected_delivery_date falls between two dates (inclusive).
 * @param {string} companyId - The company ID
 * @param {string} startDate - First day, YYYY-MM-DD
 * @param {string} endDate - Last day, YYYY-MM-DD
 * @param {Object} filters - Filter options (userId, showOnlyMine, productionFilter, quoteTypeFilters)
 * @returns {Promise<Object>} One card per required quote
 */
export const getDeliveriesInRange = async (companyId, startDate, endDate, filters = {}) => {
    try {
        // Create start and end timestamps covering every day in the range
        const startOfDay = `${startDate}T00:00:00.000Z`;
        const endOfDay = `${endDate}T23:59:59.999Z`;

        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes!inner(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    delivery_status,
                    expected_delivery_date,
                    invoiced,
                    delivery_note,
                    delivery_time_slot,
                    production_status,
                    delivery_count,
                    selected_units,
                    child_unique_id,
                    delivery_tag,
                    layout_only,
                    waiting_job,
                    sent_production,
                    quick_notes,
                    quote_playground,
                    delivery_playground,
                    group_id,
                    group_name,
                    department_file_status,
                    department_files_completed_at
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
               job_production(
                    id,
                    quote_type,
                    quote_id,
                    phase,
                    is_started_cutting,
                    is_started_production,
                    is_completed,
                    start_cutting_at,
                    start_production_at
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_id,
                    assigned_driver_name,
                    delivery_count,
                    child_id,
                    child_unique_id
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .or('is_cancelled.is.null,is_cancelled.eq.false')
            .neq("job_type", "Estimation")
            .eq('job_quotes.is_required', true)
            .eq('job_quotes.layout_only', false)
            .gte('job_quotes.expected_delivery_date', startOfDay)
            .lte('job_quotes.expected_delivery_date', endOfDay);

        // Apply user filters for delivery (filter by assigned driver)
        if (filters.userId) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.userId);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return { data: [], error: null };
            }
        } else if (filters.showOnlyMine) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.showOnlyMine);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return { data: [], error: null };
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // Process jobs to create one card per required quote
        // The query already filters by date range, so we just need to transform the data
        const processedData = [];
        data?.forEach(job => {
            const requiredQuotes = job.job_quotes?.filter(q => q.is_required && q.expected_delivery_date) || [];

            requiredQuotes.forEach(quote => {
                const quoteDeliveryCount = quote.delivery_count ?? 0;

                if (
                    quote.delivery_playground === true ||
                    quote.quote_playground === true
                ) {
                    return;
                }

                // Determine system type up-front — needed for both production matching and delivery matching.
                const isNewSystem = !!quote.child_unique_id;

                // Apply quote type filter
                if (filters.quoteTypeFilters && !filters.quoteTypeFilters[quote.quote_type]) {
                    // Skip layout-only departments from delivery schedule
                    if (quote.layout_only) return;

                    return; // Skip this quote if its type is filtered out
                }

                // Check production status using the matched job_production row (authoritative)
                // as well as job_quotes.production_status (may lag for new-system jobs).
                const matchedProd =
                    job.job_production?.find(p => p.quote_id === quote.id) ||
                    job.job_production?.find(p =>
                        !p.quote_id &&
                        p.quote_type === quote.quote_type
                    );

                const isFileOnlyQuote =
                    quote.quote_type === "beams" ||
                    quote.quote_type === "flooring_sheets";

                const isProductionStarted =
                    isFileOnlyQuote ||
                    matchedProd?.is_started_cutting === true ||
                    matchedProd?.is_started_production === true ||
                    !!matchedProd?.start_cutting_at ||
                    !!matchedProd?.start_production_at ||
                    matchedProd?.phase === "in_cutting" ||
                    matchedProd?.phase === "cutting_ended" ||
                    matchedProd?.phase === "in_production" ||
                    matchedProd?.phase === "completed" ||
                    quote.production_status === "in_cutting" ||
                    quote.production_status === "cutting_ended" ||
                    quote.production_status === "in_production" ||
                    quote.production_status === "completed";

                const isProductionFinished =
                    isFileOnlyQuote ||
                    matchedProd?.is_completed === true ||
                    matchedProd?.phase === "completed" ||
                    quote.production_status === "completed";

                if (filters.productionFilter) {
                    if (filters.productionFilter === 'finished' && !isProductionFinished) {
                        return; // Skip if filtering for finished and this isn't finished
                    }
                    if (filters.productionFilter === 'not_finished' && isProductionFinished) {
                        return; // Skip if filtering for not finished and this is finished
                    }
                }
                const delivery = job.job_deliveries?.find(d =>
                    isNewSystem
                        ? d.child_unique_id === quote.id
                        : d.quote_type === quote.quote_type && (d.delivery_count ?? 0) === quoteDeliveryCount
                ) || null;

                const transformedQuoteCard = {
                    ...job,
                    quote_info: {
                        quote_type: quote.quote_type,
                        expected_finish_date: quote.expected_delivery_date,
                        design_status: quote.design_status,
                        estimation_status: quote.estimation_status,
                        production_status: quote.production_status,
                        delivery_status: quote.delivery_status,
                        invoiced: quote.invoiced,
                        id: quote.id,
                        quoteId: quote.id,                          // UUID of this job_quotes row
                        childUniqueId: quote.child_unique_id || null, // display ID for new-system
                        delivery_note: quote.delivery_note,
                        delivery_time_slot: quote.delivery_time_slot,
                        selected_units: quote.selected_units,
                        delivery_count: quote.delivery_count,
                        delivery_tag: quote.delivery_tag,
                        waiting_job: quote.waiting_job || false,
                        sent_production: quote.sent_production || false,
                        quick_notes: quote.quick_notes,
                        group_id: quote.group_id || null,
                        group_name: quote.group_name || null,
                        department_file_status: quote.department_file_status,
                        departmentFileStatus: quote.department_file_status,
                        department_files_completed_at: quote.department_files_completed_at,
                        departmentFilesCompletedAt: quote.department_files_completed_at,
                    },
                    designers: job.job_designers?.filter(d => d.quote_type === quote.quote_type) || [],
                    production: matchedProd || null,  // already resolved above using quote_id / quote_type
                    delivery
                };


                processedData.push(transformedQuoteCard);
            });
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching deliveries for date:', error);
        return { data: [], error };
    }
};

/**
 * Fetch unscheduled delivery assignments
 * NOW RETURNS QUOTE-BASED CARDS: Creates one card per required quote
 * A quote appears in unscheduled if it's missing expected_delivery_date
 * @param {string} companyId - The company ID
 * @param {Object} filters - Filter options (userId, showOnlyMine)
 * @returns {Promise<Object>} Unscheduled delivery data (one card per quote)
 */
export const getUnscheduledDeliveries = async (companyId, filters = {}) => {
    try {
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes!inner(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_delivery_date,
                    invoiced,
                    delivery_note,
                    delivery_time_slot,
                    production_status,
                    delivery_count,
                    selected_units,
                    child_unique_id,
                    delivery_tag,
                    waiting_job,
                    sent_production,
                    quick_notes,
                    quote_playground,
                    delivery_playground,
                    group_id,
                    group_name,
                    department_file_status,
                    department_files_completed_at
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
               job_production(
                    id,
                    quote_type,
                    quote_id,
                    phase,
                    is_started_cutting,
                    is_started_production,
                    is_completed,
                    start_cutting_at,
                    start_production_at
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_id,
                    assigned_driver_name,
                    delivery_count,
                    child_id,
                    child_unique_id
                )
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .or('is_cancelled.is.null,is_cancelled.eq.false')
            .eq('job_quotes.is_required', true)
            .eq('job_quotes.waiting_job', true)
            .eq('job_quotes.layout_only', false)
            // .is('job_quotes.expected_delivery_date', null)
            .neq("job_type", "Estimation");

        // Apply user filters for delivery (filter by assigned driver)
        if (filters.userId) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.userId);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return { data: [], error: null };
            }
        } else if (filters.showOnlyMine) {
            const { data: deliveryJobs } = await supabase
                .from('job_deliveries')
                .select('job_id')
                .eq('assigned_driver_id', filters.showOnlyMine);

            const jobIds = deliveryJobs?.map(d => d.job_id) || [];
            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            } else {
                return { data: [], error: null };
            }
        }

        const { data, error } = await query;

        if (error) throw error;

        // Create one card per quote, but only for quotes missing delivery date
        const processedData = [];
        data?.forEach(job => {
            const requiredQuotes = job.job_quotes?.filter(q => q.is_required) || [];

            requiredQuotes.forEach(quote => {
                const quoteDeliveryCount = quote.delivery_count ?? 0;

                if (
                    quote.delivery_playground === true ||
                    quote.quote_playground === true
                ) {
                    return;
                }

                // Determine system type up-front — needed for both production matching and delivery matching.
                // Skip layout-only departments from delivery schedule
                if (quote.layout_only) return;

                const isNewSystem = !!quote.child_unique_id;

                // Apply quote type filter
                if (filters.quoteTypeFilters && !filters.quoteTypeFilters[quote.quote_type]) {
                    return; // Skip this quote if its type is filtered out
                }

                // Check production status using the matched job_production row (authoritative)
                // as well as job_quotes.production_status (may lag for new-system jobs).
                const matchedProd =
                    job.job_production?.find(p => p.quote_id === quote.id) ||
                    job.job_production?.find(p =>
                        !p.quote_id &&
                        p.quote_type === quote.quote_type
                    );

                const isFileOnlyQuote =
                    quote.quote_type === "beams" ||
                    quote.quote_type === "flooring_sheets";

                const isProductionStarted =
                    isFileOnlyQuote ||
                    matchedProd?.is_started_cutting === true ||
                    matchedProd?.is_started_production === true ||
                    !!matchedProd?.start_cutting_at ||
                    !!matchedProd?.start_production_at ||
                    matchedProd?.phase === "in_cutting" ||
                    matchedProd?.phase === "cutting_ended" ||
                    matchedProd?.phase === "in_production" ||
                    matchedProd?.phase === "completed" ||
                    quote.production_status === "in_cutting" ||
                    quote.production_status === "cutting_ended" ||
                    quote.production_status === "in_production" ||
                    quote.production_status === "completed";

                const isProductionFinished =
                    isFileOnlyQuote ||
                    matchedProd?.is_completed === true ||
                    matchedProd?.phase === "completed" ||
                    quote.production_status === "completed";

                if (filters.productionFilter) {
                    if (filters.productionFilter === 'finished' && !isProductionFinished) {
                        return; // Skip if filtering for finished and this isn't finished
                    }
                    if (filters.productionFilter === 'not_finished' && isProductionFinished) {
                        return; // Skip if filtering for not finished and this is finished
                    }
                }

                // New-system: match delivery by child_unique_id; old-system: by quote_type + count
                const delivery = job.job_deliveries?.find(d =>
                    isNewSystem
                        ? d.child_unique_id === quote.id
                        : d.quote_type === quote.quote_type && (d.delivery_count ?? 0) === quoteDeliveryCount
                ) || null;

                // Show in unscheduled if missing delivery date
                if (!quote.expected_delivery_date) {
                    const transformedQuoteCard = {
                        ...job,
                        quote_info: {
                            quote_type: quote.quote_type,
                            expected_finish_date: quote.expected_delivery_date || null,
                            design_status: quote.design_status,
                            estimation_status: quote.estimation_status,
                            invoiced: quote.invoiced,
                            id: quote.id,
                            quoteId: quote.id,
                            childUniqueId: quote.child_unique_id || null,
                            delivery_note: quote.delivery_note,
                            delivery_time_slot: quote.delivery_time_slot,
                            selected_units: quote.selected_units,
                            production_status: quote.production_status,
                            delivery_count: quote.delivery_count,
                            delivery_tag: quote.delivery_tag,
                            waiting_job: quote.waiting_job || false,
                            sent_production: quote.sent_production || false,
                            quick_notes: quote.quick_notes,
                            group_id: quote.group_id || null,
                            group_name: quote.group_name || null,
                            department_file_status: quote.department_file_status,
                            departmentFileStatus: quote.department_file_status,
                            department_files_completed_at: quote.department_files_completed_at,
                            departmentFilesCompletedAt: quote.department_files_completed_at,
                        },
                        designers: job.job_designers?.filter(d => d.quote_type === quote.quote_type) || [],
                        production: matchedProd || null,
                        delivery: delivery || null
                    };

                    processedData.push(transformedQuoteCard);
                }
            });
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching unscheduled deliveries:', error);
        return { data: [], error };
    }
};

/**
 * Update delivery expected date for a specific quote
 * @param {string} jobId - The job ID
 * @param {string} quoteType - The quote type (roof, wall, floor, timber)
 * @param {Date|null} newDate - The new date or null to unschedule
 * @param {number} deliveryCount - The delivery count (0 for first delivery, 1 for rescheduled, etc.)
 * @returns {Promise<Object>} Result of the update
 */
export const updateDeliveryDate = async (jobId, quoteType, newDate, deliveryCount = 0, quoteId = null) => {
    try {
        const updateData = {
            expected_delivery_date: newDate ? newDate.toISOString() : null,
            updated_at: new Date().toISOString()
        };

        let query = supabase.from('job_quotes').update(updateData);

        if (quoteId) {
            // New-system (or any case where we have the UUID): update by PK — precise and safe.
            query = query.eq('id', quoteId);
        } else {
            // Old-system fallback: update by job_id + quote_type + delivery_count.
            query = query.eq('job_id', jobId).eq('quote_type', quoteType);
            if (deliveryCount === 0) {
                query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
            } else {
                query = query.eq('delivery_count', deliveryCount);
            }
        }

        const { data, error } = await query.select();

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error updating delivery date:', error);
        return { data: null, error };
    }
};

export const updateWaitingStatus = async (quoteId, quoteType, jobId, deliveryCount) => {
    try {


        const updateData = {
            waiting_job: true,
            updated_at: new Date().toISOString()
        };

        let query = supabase.from('job_quotes').update(updateData);

        if (quoteId) {
            // New-system (or any case where we have the UUID): update by PK — precise and safe.
            query = query.eq('id', quoteId);
        } else {
            // Old-system fallback: update by job_id + quote_type + delivery_count.
            query = query.eq('job_id', jobId).eq('quote_type', quoteType);
            if (deliveryCount === 0) {
                query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
            } else {
                query = query.eq('delivery_count', deliveryCount);
            }
        }

        const { data, error } = await query.select();

        if (error) throw error;

        return { data, error: null };
    } catch (error) {
        console.error('Error updating waiting status:', error);
        return { data: null, error };
    }
}

/**
 * Fetch all delivery assignments that are pinned to the delivery playground.
 * Ignores scheduling status — returns cards regardless of which week they are on.
 */
export const getDeliveryPlaygroundAssignments = async (companyId) => {
    try {
        const { data: pgQuotes, error: pgError } = await supabase
            .from('job_quotes')
            .select('id, job_id')
            .or('delivery_playground.eq.true,quote_playground.eq.true');

        if (pgError) throw pgError;
        if (!pgQuotes?.length) return { data: [], error: null };

        const pgQuoteIds = new Set(pgQuotes.map(q => q.id));
        const jobIds = [...new Set(pgQuotes.map(q => q.job_id))];

        const { data, error } = await supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(
                    id,
                    quote_type,
                    is_required,
                    design_status,
                    estimation_status,
                    expected_delivery_date,
                    invoiced,
                    delivery_note,
                    delivery_time_slot,
                    production_status,
                    delivery_count,
                    selected_units,
                    child_unique_id,
                    delivery_tag,
                    waiting_job,
                    sent_production,
                    quote_playground,
                    delivery_playground,
                    quick_notes,
                    group_id,
                    group_name
                ),
                job_designers(
                    id,
                    quote_type,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
                job_estimators(
                    id,
                    user_id,
                    name,
                    is_started,
                    is_completed
                ),
              job_production(
                    id,
                    quote_type,
                    quote_id,
                    phase,
                    is_started_cutting,
                    is_started_production,
                    is_completed,
                    start_cutting_at,
                    start_production_at
                ),
                job_deliveries(
                    id,
                    quote_type,
                    delivery_status,
                    assigned_driver_id,
                    assigned_driver_name,
                    delivery_count,
                    child_id,
                    child_unique_id
                )
            `)
            .in('id', jobIds)
            .eq('company_id', companyId)
            .eq('job_quotes.layout_only', false)
            .eq('deleted', false);

        if (error) throw error;

        const processedData = [];

        (data || []).forEach(job => {
            job.job_quotes?.filter(q => pgQuoteIds.has(q.id)).forEach(quote => {
                const quoteDeliveryCount = quote.delivery_count ?? 0;
                const isNewSystem = !!quote.child_unique_id;

                const matchedProd =
                    job.job_production?.find(p => p.quote_id === quote.id) ||
                    job.job_production?.find(p =>
                        !p.quote_id &&
                        p.quote_type === quote.quote_type
                    );

                const delivery = job.job_deliveries?.find(d =>
                    isNewSystem
                        ? d.child_unique_id === quote.id
                        : d.quote_type === quote.quote_type && (d.delivery_count ?? 0) === quoteDeliveryCount
                ) || null;

                processedData.push({
                    ...job,
                    quote_info: {
                        quote_type: quote.quote_type,
                        expected_finish_date: quote.expected_delivery_date || null,
                        design_status: quote.design_status,
                        estimation_status: quote.estimation_status,
                        invoiced: quote.invoiced,
                        id: quote.id,
                        quoteId: quote.id,
                        childUniqueId: quote.child_unique_id || null,
                        delivery_note: quote.delivery_note,
                        delivery_time_slot: quote.delivery_time_slot,
                        selected_units: quote.selected_units,
                        production_status: quote.production_status,
                        delivery_count: quote.delivery_count,
                        delivery_tag: quote.delivery_tag,
                        waiting_job: quote.waiting_job || false,
                        sent_production: quote.sent_production || false,
                        delivery_playground: true,
                        quick_notes: quote.quick_notes,
                        group_id: quote.group_id || null,
                        group_name: quote.group_name || null,
                    },
                    designers: job.job_designers?.filter(d => d.quote_type === quote.quote_type) || [],
                    production: matchedProd || null,
                    delivery: delivery || null,
                });
            });
        });

        return { data: processedData, error: null };
    } catch (error) {
        console.error('Error fetching delivery playground assignments:', error);
        return { data: [], error };
    }
}

/**
 * Commit a card into a group — sets group_id, group_name, AND expected_delivery_date
 * in ONE update so the realtime subscription receives the final state in a single event.
 * Using two separate calls would cause an intermediate realtime event with the old date,
 * making the card appear to jump back to its original day before snapping to the group.
 *
 * @param {string}           quoteId          - job_quotes.id
 * @param {string}           groupId
 * @param {string}           groupName
 * @param {string|null|undefined} targetDate  - new delivery date (undefined = don't touch the column)
 * @param {boolean|undefined}    targetPlayground - true/false to migrate between playground and schedule;
 *                                                  undefined = leave as-is (no cross-section move)
 */
export const commitGroupMember = async (quoteId, groupId, groupName, targetDate, targetPlayground) => {
    try {
        const updateData = {
            group_id: groupId,
            group_name: groupName,
            updated_at: new Date().toISOString(),
        };
        // Only write the date when it's actually changing
        if (targetDate !== undefined) {
            updateData.expected_delivery_date = targetDate ? new Date(targetDate).toISOString() : null;
        }
        // When the card crosses the playground boundary, update the flag in the same atomic write
        // to avoid an intermediate realtime event placing the card in the wrong section.
        if (targetPlayground !== undefined) {
            updateData.delivery_playground = targetPlayground;
            updateData.quote_playground = targetPlayground;

            if (targetPlayground === true) {
                updateData.expected_delivery_date = null;
            }
        }
        const { error } = await supabase
            .from('job_quotes')
            .update(updateData)
            .eq('id', quoteId);
        return { error: error || null };
    } catch (error) {
        console.error('Error committing group member:', error);
        return { error };
    }
};

/**
 * Set or clear the group for a single job_quotes row
 */
export const setDeliveryGroup = async (quoteId, groupId, groupName) => {
    try {
        const { error } = await supabase
            .from('job_quotes')
            .update({ group_id: groupId, group_name: groupName })
            .eq('id', quoteId);
        return { error };
    } catch (error) {
        return { error };
    }
};

/**
 * Disband a group — clears group_id/group_name from all quotes that had this group
 */
export const disbandDeliveryGroup = async (groupId) => {
    try {
        const { error } = await supabase
            .from('job_quotes')
            .update({ group_id: null, group_name: null })
            .eq('group_id', groupId);
        return { error };
    } catch (error) {
        return { error };
    }
};

/**
 * Set the delivery date for every quote in a group — single DB call so realtime
 * fires once for the whole group instead of once per member.
 *
 * @param {string}    groupId - The group_id shared by all members
 * @param {Date|null} newDate - Target date (or null to unschedule)
 */
export const setGroupDeliveryDate = async (groupId, newDate) => {
    try {
        const { error } = await supabase
            .from('job_quotes')
            .update({
                expected_delivery_date: newDate ? newDate.toISOString() : null,
                delivery_playground: false,
                quote_playground: false,
                updated_at: new Date().toISOString(),
            })
            .eq('group_id', groupId);
        return { error: error || null };
    } catch (error) {
        console.error('Error setting group delivery date:', error);
        return { error };
    }
};

/**
 * Move entire group to waiting (unscheduled) — clears date and playground flag,
 * sets waiting_job = true. Single DB call so realtime fires once per member.
 */
export const setGroupWaiting = async (groupId) => {
    try {
        const { error } = await supabase
            .from('job_quotes')
            .update({
                expected_delivery_date: null,
                delivery_playground: false,
                quote_playground: false,
                waiting_job: true,
                updated_at: new Date().toISOString(),
            })
            .eq('group_id', groupId);
        return { error: error || null };
    } catch (error) {
        console.error('Error moving group to waiting:', error);
        return { error };
    }
};

/**
 * Set delivery_playground for every quote in a group.
 * When pinning to playground the date is also cleared so the cards don't
 * appear in any day column on other clients.
 */
export const setGroupPlayground = async (groupId, isPlayground) => {
    try {
        const updateData = {
            delivery_playground: isPlayground,
            quote_playground: isPlayground,
            updated_at: new Date().toISOString(),
        };
        if (isPlayground) {
            updateData.expected_delivery_date = null; // clear date when entering playground
        }
        const { error } = await supabase
            .from('job_quotes')
            .update(updateData)
            .eq('group_id', groupId);
        return { error: error || null };
    } catch (error) {
        console.error('Error setting group playground:', error);
        return { error };
    }
};

/**
 * Assign a driver to multiple job_deliveries rows at once — single DB call so
 * realtime fires once for the whole group instead of once per member.
 *
 * @param {string[]} deliveryIds        - Array of job_deliveries.id to update
 * @param {string}   driverId
 * @param {string}   driverName
 * @param {string}   assignedByUserId
 * @param {string}   assignedByName
 */
export const setGroupDriverBatch = async (deliveryIds, driverId, driverName, assignedByUserId, assignedByName) => {
    if (!deliveryIds?.length) return { error: null };
    try {
        const { error } = await supabase
            .from('job_deliveries')
            .update({
                assigned_driver_id: driverId,
                assigned_driver_name: driverName,
                assigned_at: new Date().toISOString(),
                assigned_by_user_id: assignedByUserId,
                assigned_by_name: assignedByName,
                delivery_status: 'scheduled',
            })
            .in('id', deliveryIds);
        return { error: error || null };
    } catch (error) {
        console.error('Error setting group driver batch:', error);
        return { error };
    }
};